/**
 * The first-run tour's steps (SPEC §8), as data. Each step names the panel it
 * spotlights and the commands it issues on entry. The tour drives the editor
 * only through these commands (origin `tour`).
 *
 * Copy is editor UI copy: each step is titled with its panel's name, and says
 * what it's for in a line or two, as reference text, not a pitch.
 */
import type { CommandRequest } from "../../commands/types";
import { enabledComponent } from "../../world/entity";
import type { EntityId, EntityRecord } from "../../world/entity";
import type { WorldSnapshot } from "../../world/World";
import { navHints } from "../viewport/navHints";

/** Spotlight targets registered by the panels, and the toolbar's Play button (PanelRectRegistry). */
export type TourTarget =
  "viewport" | "contentBrowser" | "outliner" | "inspector" | "console" | "play";

export interface TourStep {
  readonly id: string;
  readonly target: TourTarget;
  /** Used in the previous step's "Next: …" button. */
  readonly shortName: string;
  readonly title: string;
  readonly body: string;
  /** The body where the pointer is a finger, when the mouse's wording doesn't apply there. */
  readonly touchBody?: string;
  /** Commands issued when the step is entered, from the open level. */
  readonly enter: (world: WorldSnapshot) => readonly Omit<CommandRequest, "origin">[];
}

export const TOUR_STEPS: readonly TourStep[] = [
  {
    id: "viewport",
    target: "viewport",
    shortName: "Viewport",
    title: "Viewport",
    body: "RMB + WASD fly · Alt + LMB orbit · MMB pan · Wheel dolly · F frame selection · W/E/R move, rotate, scale",
    // The gestures, as the viewport's first-run hint lists them.
    touchBody: navHints("coarse")
      .map(([gesture, action]) => `${gesture} ${action}`)
      .join(" · "),
    enter: () => [],
  },
  {
    id: "contentBrowser",
    target: "contentBrowser",
    shortName: "Content Browser",
    title: "Content Browser",
    body: "Roles and projects, one level each, newest first. Double-click to open. Also lists assets such as models.",
    touchBody:
      "Roles and projects, one level each, newest first. Tap to open. Also lists assets such as models.",
    enter: () => [{ type: "panel.focus", payload: { panel: "contentBrowser" } }],
  },
  {
    id: "outliner",
    target: "outliner",
    shortName: "Outliner",
    title: "Outliner",
    body: "Entities in the open level. Selection is shared with the viewport.",
    enter: (world) => {
      // Brought forward where it can be hidden: a phone's sheet shows one panel at a time.
      const focus = { type: "panel.focus", payload: { panel: "outliner" } };
      const zone = tourZone(world);
      if (!zone) return [focus];
      // Transient: the tour leaves the undo stack as it found it (A-15). The
      // view stays where it is, so the steps around this one don't jump.
      return [focus, { type: "selection.set", payload: { ids: [zone], transient: true } }];
    },
  },
  {
    id: "inspector",
    target: "inspector",
    shortName: "Inspector",
    title: "Inspector",
    body: "Components of the selected entity.",
    enter: () => [{ type: "panel.focus", payload: { panel: "inspector" } }],
  },
  {
    id: "console",
    target: "console",
    shortName: "Console",
    title: "Console",
    body: "Logs from the engine show up here.",
    enter: () => [{ type: "panel.focus", payload: { panel: "console" } }],
  },
  {
    id: "play",
    target: "play",
    shortName: "Play",
    title: "Play",
    body: "Click the Play button to run the level. Use the arrow keys or UI to move between zones.",
    touchBody: "Tap the Play button to run the level. Swipe or use the UI to move between zones.",
    enter: () => [],
  },
];

/**
 * The level's newest zone, the current role, which the startup view already
 * shows (on the startup level, Zone_Staff_2025). A level whose zones hold no
 * dated role (a project) gives its first zone.
 */
function tourZone(world: WorldSnapshot): EntityId | null {
  const zones = [...world.entities.values()].filter(
    (entity) => enabledComponent(entity, "Zone") !== undefined,
  );
  const dated = zones.filter((zone) => startOf(zone) !== null);
  const newest = dated.sort((a, b) => (startOf(b) ?? "").localeCompare(startOf(a) ?? ""))[0];
  return (newest ?? zones[0])?.id ?? null;
}

/** When a zone's role began ("YYYY-MM"), or null when it holds no dated role. */
function startOf(zone: EntityRecord): string | null {
  const start = enabledComponent(zone, "Role")?.start;
  return typeof start === "string" ? start : null;
}
